import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { Column } from 'primereact/column'
import Badge, { type BadgeTone } from '../../../components/ui/Badge'
import { formatDate } from '../../../lib/datetime'
import { DEADLINE_REQUEST_STATUS, type DeadlineRequest } from '../types'
import DeadlineRequestRowActions from './DeadlineRequestRowActions'

interface ColumnOpts {
  t: TFunction
  onApprove: (row: DeadlineRequest) => void
  onReject: (row: DeadlineRequest) => void
  /** İşlem süren satır — yalnız o satırın menüsü kilitlenir (078-b `busyId` deseni). */
  busyId: number | null
  /**
   * "İşlemler" sütunu hiç kurulsun mu (kullanıcı kararı 2026-08-14): **yalnız Onay Bekleyenler**
   * sekmesinde. "Taleplerim"de kullanıcı kendi talebine karar veremez, sütun boş kalıyordu.
   *
   * <p>İşlev kaybı yok: kullanıcı hem talebi giren hem (kök aksiyonu oluşturduğu için) onay mercii
   * olduğu kenar durumda talep <b>Onay Bekleyenler</b> sekmesinde de listelenir ve karar orada verilir.
   */
  showActions: boolean
}

/**
 * Genişlik sınıfını **hem başlık hem gövde** hücresine verir.
 *
 * <p><b>Zorunlu:</b> PrimeReact `Column`'un `className` prop'u yalnız <b>gövde</b> hücresine (`td`)
 * uygulanır; başlık hücresi (`th`) ayrı bir prop olan `headerClassName`'i okur (bkz.
 * `datatable.cjs.js` — gövde: `classNames(bodyClassName, getColumnProp('className'), …)`, başlık:
 * `classNames(headerClassName, …)`). `table-layout: fixed` ise sütun genişliğini <b>ilk satırdan</b>
 * (başlık satırı) hesaplar → sınıf yalnız `td`'ye verilirse genişlikler **sessizce yok sayılır** ve
 * tarayıcı sütunları kendi dağıtır. 2026-08-14'te tam olarak bu yaşandı: yüzdeler iki tur
 * değiştirildi, ekranda hiçbir şey değişmedi.
 *
 * @param width genişlik sınıfı — `th` ve `td`'ye birlikte gider
 * @param cell yalnız gövdeye gidecek ek sınıf (ör. `tk-cell-wrap` — kuralı `td.tk-cell-wrap`'tır)
 */
function colClass(width: string, cell?: string): { className: string; headerClassName: string } {
  return { className: cell ? `${width} ${cell}` : width, headerClassName: width }
}

/**
 * Talep durumu → rozet tonu. Kimlikler `REFERANS(TYPE='DEADLINE_UPDATE_REQUEST_STATUS')`
 * kataloğundandır ve DB CHECK kısıtıyla **kapalı bir kümedir** (400/401/402/403).
 *
 * <p>**İptal (403) nötr gridir**, kırmızı değil: ret bir *karardır* (birinin talebi reddetti), iptal ise
 * talebin **konusunun kalmaması**dır — görev kapandığı için sistem düşürür. İkisi aynı renkte olsaydı
 * talep sahibi reddedildiğini sanardı.
 *
 * <p>Etiket **metni** koda gömülmez — sunucudan dile göre çözülmüş `statusName` gelir; burada yalnız
 * <b>renk</b> eşlenir.
 */
export function requestStatusTone(statusId: number): BadgeTone {
  switch (statusId) {
    case DEADLINE_REQUEST_STATUS.APPROVED:
      return 'green'
    case DEADLINE_REQUEST_STATUS.REJECTED:
      return 'red'
    case DEADLINE_REQUEST_STATUS.CANCELLED:
      return 'neutral'
    default:
      return 'blue'
  }
}

/**
 * Hedef Teslim Tarihi Güncelleme Talebi tablosu sütunları (087/FR-013).
 *
 * <p><b>Her alan kendi sütununda durur</b> (kullanıcı kararı 2026-08-14); yerleşim sütun
 * <b>genişlikleriyle</b> çözülür, sütun birleştirerek değil. Genişlikler `deadlineRequests.css`'te
 * içerik türüne göre paylaştırılır (`table-layout: fixed`) → tablo kapsayıcıya sığar, yatay kaydırma
 * oluşmaz; tarihler satır ortasında bölünmez.
 *
 * <p><b>Uzun metin kırpılmaz, SARAR</b> (kullanıcı bildirimi 2026-08-14: iki satırlık kırpma veri
 * kaybı gibi görünüyordu). Serbest metin taşıyan sütunlar paylaşılan <b>{@code tk-cell-wrap}</b>
 * değiştiricisini alır — global {@code td { white-space: nowrap }} kuralını hücre bazında açan tek
 * kaynak; sarma <b>kelime bazlıdır</b>. Tarih/rozet/kebab sütunlarına verilmez (atomik değerlerde
 * doğru davranış tek satırdır). {@code title} nitelikleri kalır: artık tek erişim yolu değil, ek
 * kolaylık.
 *
 * <p>Hiçbir sütun `sortable` <b>değildir</b>: düzen sunucuda sabittir (en yeni talep üstte); istemci
 * sıralaması yalnız görünen sayfayı sıralar ve kullanıcıyı yanıltırdı.
 *
 * <p><b>Onayla/Reddet</b> yalnız {@link DeadlineRequest.canDecide} olan satırlarda çıkar ve
 * {@link DeadlineRequestRowActions} içinde iki ayrı buton olarak görünür (kullanıcı kararı
 * 2026-08-21 — önceki kebab menüsü kaldırıldı, işlemler doğrudan görünür olsun istendi). Bayrağı
 * <b>sunucu</b> hesaplar; istemci kuralı yeniden hesaplamaz.
 *
 * <p><b>Kaldırılan sütunlar</b> (kullanıcı kararları 2026-08-14): <b>Onay Mercii</b> — iki sekmeli
 * düzende bilgi sekmenin kendisinden okunuyor ("Onay Bekleyenler"deki her satırın mercii oturum
 * kullanıcısıdır); <b>Aksiyon Durumu</b> — talep kararı için gerekli olmayan bağlam, tabloyu
 * daraltıyordu. İkisinin de DTO alanı (`approverUserName`, `actionStatusId`/`actionStatusName`)
 * <b>korundu</b> — sözleşme daraltılmadı, yalnız bu ekran onları göstermiyor.
 *
 * <p><b>Aksiyon Başlığı hücresinde madde numarası GÖSTERİLMEZ</b> (kullanıcı isteği 2026-08-17): hücre
 * eskiden iki satırlıydı (ad + `actionRecordNumber`); numara talep kararı için gerekli değil ve satırı
 * yükseltiyordu. Alan API yanıtında **durmaya devam eder** — sözleşme daraltılmadı.
 *
 * <p><b>Talep Tarihi ve Onay/Ret Tarihi</b> (kullanıcı kararı 2026-08-14, ikinci tur): talebin ne zaman
 * girildiği ve ne zaman karara bağlandığı satırda görünür. Talep tarihi bir kez kaldırılmış, sonra
 * geri istenmişti — alan (`creDate`) hiç kaldırılmadığı, yalnız gösterilmediği için geri getirmek
 * sözleşme değişikliği gerektirmedi. Onay/Ret tarihi (`approveOrRejectTime`) Beklemede taleplerde
 * boştur. İkisi de <b>tarih</b> olarak biçimlenir (saat gösterilmez — listede karar için gereken
 * çözünürlük gün düzeyindedir).
 */
export function deadlineRequestColumns({
  t,
  onApprove,
  onReject,
  busyId,
  showActions,
}: ColumnOpts): ReactElement[] {
  return [
    <Column
      key="action"
      header={t('deadlineRequests.column.action')}
      {...colClass('dr-col-action', 'tk-cell-wrap')}
      body={(row: DeadlineRequest) => (
        <span title={row.actionName ?? undefined}>{row.actionName}</span>
      )}
    />,
    <Column
      key="currentDeadline"
      header={t('deadlineRequests.column.currentDeadline')}
      {...colClass('dr-col-date')}
      body={(row: DeadlineRequest) => (
        <span className="dr-nowrap">{dateCell(row.currentDeadline)}</span>
      )}
    />,
    <Column
      key="requestedDate"
      header={t('deadlineRequests.column.requestedDate')}
      {...colClass('dr-col-date')}
      body={(row: DeadlineRequest) => (
        // Talep edilen tarih vurgulanır: mevcutla farkı (FR-020a) bir bakışta görünsün.
        <span className="dr-nowrap dr-date-new">{dateCell(row.requestedDate)}</span>
      )}
    />,
    <Column
      key="requestNote"
      header={t('deadlineRequests.column.requestNote')}
      {...colClass('dr-col-note', 'tk-cell-wrap')}
      body={(row: DeadlineRequest) => <span title={row.requestNote}>{row.requestNote}</span>}
    />,
    <Column
      key="status"
      header={t('deadlineRequests.column.status')}
      {...colClass('dr-col-status')}
      body={(row: DeadlineRequest) => (
        <Badge tone={requestStatusTone(row.statusId)}>{row.statusName ?? ''}</Badge>
      )}
    />,
    <Column
      key="rejectNote"
      header={t('deadlineRequests.column.rejectNote')}
      {...colClass('dr-col-reject', 'tk-cell-wrap')}
      body={(row: DeadlineRequest) => (
        <span title={row.rejectNote ?? undefined}>{row.rejectNote}</span>
      )}
    />,
    <Column
      key="requestUser"
      header={t('deadlineRequests.column.requestUser')}
      {...colClass('dr-col-user', 'tk-cell-wrap')}
      body={(row: DeadlineRequest) => (
        <span title={row.requestUserName ?? undefined}>{row.requestUserName}</span>
      )}
    />,
    <Column
      key="requestDate"
      header={t('deadlineRequests.column.requestDate')}
      {...colClass('dr-col-audit-date')}
      body={(row: DeadlineRequest) => <span className="dr-nowrap">{dateCell(row.creDate)}</span>}
    />,
    <Column
      key="decisionDate"
      header={t('deadlineRequests.column.decisionDate')}
      {...colClass('dr-col-audit-date')}
      body={(row: DeadlineRequest) => (
        // Beklemede taleplerde boş kalır (henüz karar yok) — yer tutucu yazılmaz.
        <span className="dr-nowrap">{dateCell(row.approveOrRejectTime)}</span>
      )}
    />,
    ...(showActions
      ? [
          <Column
            key="actions"
            header={t('deadlineRequests.column.actions')}
            {...colClass('dr-col-actions')}
            body={(row: DeadlineRequest) =>
              row.canDecide ? (
                <DeadlineRequestRowActions
                  row={row}
                  onApprove={onApprove}
                  onReject={onReject}
                  busy={busyId === row.id}
                />
              ) : null
            }
          />,
        ]
      : []),
  ]
}

/**
 * Tarih hücresi — değer yoksa **boş** bırakılır (anayasa: tablo hücrelerinde `-`/`—` gibi yer tutucu
 * yazılmaz). Paylaşılan {@code formatDate} boş girdide `—` döndürdüğü için burada ayrıca kapılanır.
 */
function dateCell(iso: string | null): string {
  return iso ? formatDate(iso) : ''
}
